import React, { useState, useEffect } from 'react'
import {
  MotifModal,
  MotifModalHeader,
  MotifModalBody,
  MotifIcon,
  MotifModalFooter,
  MotifButton,
  MotifErrorMessage,
  MotifSearch,
} from '@ey-xd/motif-react'
import { useForm, Controller } from 'react-hook-form'
import { AddApplicationFormType } from './type'
import ControlInputBox from '@/components/Form/InputBox/ControlInputBox'
import {
  useEditApplicationMutation,
  useSaveApplicationMutation,
  useLazyListApplicationFilterQuery,
} from '@/services/application/applicationReducer'
import { useParams } from 'react-router-dom'

type Props = {
  isVisible: boolean
  setIsVisible: (arg0: string) => void
  application: {
    applicationName: string
    description: string
    id?: string
  } | null
  editApp?: {} | null
}

export default function AddApplicationModal({
  isVisible,
  setIsVisible,
  application,
  editApp,
}: Props) {
  const [applicationList, setApplicationList] = useState<any[]>([])
  const [applicationDescription, setApplicationDescription] = useState('')
  const { engagementId } = useParams<{ engagementId: string }>()
  const [saveApplication, { isLoading }] = useSaveApplicationMutation()
  const [editApplication] = useEditApplicationMutation()
  const [listApplicationFilter] = useLazyListApplicationFilterQuery()
  const {
    handleSubmit,
    control,
    getValues,
    setValue,
    formState: { errors, isDirty },
    reset,
  } = useForm<AddApplicationFormType>({
    defaultValues: {
      applicationName: '',
      applicationDescription: '',
    }, 
    resetOptions: {
      keepDirtyValues: false, 
      keepErrors: false, 
    },
  })

  const handleAddAplicationCancel = async () => {
    setIsVisible('cancel')
    reset()
  }

  const onSubmit = async (formData: AddApplicationFormType) => {
    const applicationData = {
      applicationName: formData.applicationName,
      engagementId: engagementId,
      description: formData.applicationDescription,
    }
    let response
    if (application && application.id) {
      response = await editApplication({
        applicationId: application.id,
        formData: applicationData,
      })
    } else {
      response = await saveApplication(applicationData)
    }

    if (response && response.hasOwnProperty('data')) {
      setIsVisible('create')
      reset()
    }
  }

  useEffect(() => {
    if (isVisible && application) {
      setValue('applicationName', application.applicationName)
      setValue('applicationDescription', application.description)
      setApplicationDescription(application.description || '')
    } else {
      reset({
        applicationName: '',
        applicationDescription: ''
      })
    }
  }, [isVisible, application, setValue, reset])

  const handleApplicationNameChange = async (value: string) => {
    setValue('applicationName', value)
    if (value && value.length > 150) {
      setValue('applicationName', value.slice(0, 150))
    }
    const response = await listApplicationFilter({
      engagementId: engagementId,
      query: value,
    })
    if (response && response.hasOwnProperty('data')) {
      setApplicationList(response.data.pagedData || [])
    }
  }
  const handleSelectedApplication = (item: string | object | undefined) => {
    if (typeof item !== 'string') return
    const selectedApp = applicationList.find(
      (app) => app.applicationName === item,
    )
    if (selectedApp) {
      setValue('applicationName', selectedApp.applicationName)
      setValue('applicationDescription', selectedApp.description)
    }
  }
  
  return (
    <>
      <MotifModal
        onClose={() => handleAddAplicationCancel()}
        show={isVisible}
        size="lg"
        focusTrapOptions={{
          tabbableOptions: {
            displayCheck: 'none',
          },
        }}
      >
        <MotifModalHeader className="no-border-bottom">
          <div className="motif-h6-default-regular">
            {' '}
            {application && application.id
              ? 'Edit application'
              : 'Add application'}
          </div>
        </MotifModalHeader>
        <MotifModalBody>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="motif-row">
              <div className="motif-col-lg-12">
                <div className="form-field application-name-field">
                  <Controller
                    name="applicationName"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <MotifSearch
                        {...field}
                        aria-label="Application Name"
                        placeholder="Ex : Application Name_V 0.1"
                        maxLength={150}
                        items={applicationList.map(
                          (app) => app.applicationName,
                        )}
                        onChange={(e) =>
                          handleApplicationNameChange(e.target.value)
                        }
                        onSelect={(item) => handleSelectedApplication(item)}
                        autoComplete="off"
                      />
                    )}
                  />
                  {errors['applicationName'] &&
                    errors['applicationName'].type === 'required' && (
                      <MotifErrorMessage>
                        Application name is required
                      </MotifErrorMessage>
                    )}
                </div>
              </div>
              <div className="motif-col-lg-12">
                <div className="form-field">
                  <ControlInputBox
                    name={'applicationDescription'}
                    control={control}
                    required={true}
                    isTextArea={true}
                    maxLength="500"
                    label={'Application Description'}
                    showLabel={true}
                    placeholder={application?.description}
                  />
                  {errors['applicationDescription'] &&
                    errors['applicationDescription'].type === 'required' && (
                      <MotifErrorMessage>
                        Application description is required
                      </MotifErrorMessage>
                    )}
                </div>
              </div>
            </div>
          </form>
        </MotifModalBody>
        <MotifModalFooter>
          <div className="motif-row justify-content-end gap-1">
            <MotifButton
              onClick={() => handleAddAplicationCancel()}
              size="medium"
              type="button"
              variant="secondary"
            >
              Cancel
            </MotifButton>
            <MotifButton
              className=""
              onClick={handleSubmit(onSubmit)}
              size="medium"
              type="submit"
              variant="primary"
              loading={isLoading}
              disabled={isLoading}
            >
              {application && application.id ? 'Update' : 'Confirm'}
            </MotifButton>
          </div>
        </MotifModalFooter>
      </MotifModal>
    </>
  )
}
